import { NO_DEFAULT_SELECTED_FILTER_VALUE } from "shared/containers/Filters/defaultSelectedFilters";
import { SearchQuery } from "shared/data/reducers/types/storage";
import { getFlightCombinationsTree } from "shared/gol-api/flightSearch";
import FlightOffer from "shared/types/flightOffer";
import FlightStream from "shared/types/flightStream";

import isDirectOnly from "../isDirectOnly";
import filterAirportsMobile from "./filterAirportsMobile";

interface WindowWithNext extends Window {
  next: {
    version: string;
    router: any;
    emmiter: any;
  };
}

declare let window: WindowWithNext;

const getAvailableFlightCombinations = (flightOffer: FlightOffer) => {
  const availableFlightStreamKeys = new Set(
    flightOffer.FlightItinerary.FlightStream.flatMap((flightStream) =>
      flightStream.map(({ Key }) => Key)
    )
  );

  return flightOffer.FlightCombinations.filter((flightCombination) =>
    flightCombination.FlightOptionCombinations.FlightOptionCombination.every(
      ({ Key }) => availableFlightStreamKeys.has(Key)
    )
  );
};

const splitTransferFilterValue = (
  filterValue: string | number | boolean | undefined
): string[] => (filterValue ? String(filterValue).split("-") : []);

const areAllTransfersSelected = (
  selectedTransfers: string[],
  allTransfers: string[]
): boolean =>
  allTransfers.length > 0 &&
  allTransfers.every((transfer) => selectedTransfers.includes(transfer));

const isDirectFlightMatchingTransferFilter = (
  selectedTransfers: string[],
  allTransfers: string[]
): boolean =>
  selectedTransfers.includes(NO_DEFAULT_SELECTED_FILTER_VALUE) ||
  areAllTransfersSelected(selectedTransfers, allTransfers);

const filterAirports = (
  searchQuery: SearchQuery,
  transferAirportsByFlightStream: string[][] = []
) => (flightOffer: FlightOffer) => {
  let ret = true;
  const transferFiltersDisabled = isDirectOnly(searchQuery.max_transfers);
  const airportSearchQuery = transferFiltersDisabled
    ? { ...searchQuery, airports_transfers: undefined }
    : searchQuery;

  const paramKeys = Object.keys(airportSearchQuery).filter(
    (param) =>
      param.includes("airports_") &&
      airportSearchQuery[param] &&
      (!transferFiltersDisabled || !param.includes("_transfers"))
  );
  const isMobileRuntime = typeof window === "undefined" || !window?.next;

  // Exclusively for mobile as a fallback for no airports filters selected
  if (
    paramKeys.length === 0 &&
    !airportSearchQuery.airports_from &&
    !airportSearchQuery.airports_to &&
    !airportSearchQuery.airports_transfers &&
    isMobileRuntime
  ) {
    return true;
  }

  if (
    airportSearchQuery.airports_from ||
    airportSearchQuery.airports_to ||
    airportSearchQuery.airports_transfers
  ) {
    return filterAirportsMobile(flightOffer, airportSearchQuery);
  }

  paramKeys.forEach((key) => {
    const flightStreamIndex = Number(key.charAt(9));

    const allowedFlights: FlightStream[] = flightOffer.FlightItinerary
      .FlightStream[flightStreamIndex]
      ? flightOffer.FlightItinerary.FlightStream[flightStreamIndex].filter(
          (flightOption: FlightStream) => {
            const length = flightOption.FlightSegments.FlightSegment.length - 1;

            if (key.includes("_from")) {
              return searchQuery[key].includes(
                flightOption.FlightSegments.FlightSegment[0].OriginAirport
              );
            }

            if (key.includes("_to")) {
              return searchQuery[key].includes(
                flightOption.FlightSegments.FlightSegment[length]
                  .DestinationAirport
              );
            }

            if (key.includes("_transfers")) {
              const selectedTransfers = splitTransferFilterValue(
                searchQuery[key]
              );

              if (length === 0) {
                return isDirectFlightMatchingTransferFilter(
                  selectedTransfers,
                  transferAirportsByFlightStream[flightStreamIndex] || []
                );
              }

              for (let i = 1; i <= length; i++) {
                if (
                  selectedTransfers.includes(
                    flightOption.FlightSegments.FlightSegment[i].OriginAirport
                  )
                ) {
                  return true;
                }
              }
            }

            return false;
          }
        )
      : [];

    if (allowedFlights.length !== 0) {
      // eslint-disable-next-line no-param-reassign
      flightOffer.FlightItinerary.FlightStream[
        flightStreamIndex
      ] = allowedFlights;
    } else {
      ret = false;
    }
  });

  if (!ret) {
    return false;
  }

  const availableFlightCombinations = getAvailableFlightCombinations(
    flightOffer
  );

  if (availableFlightCombinations.length === 0) {
    return false;
  }

  if (
    availableFlightCombinations.length !== flightOffer.FlightCombinations.length
  ) {
    // eslint-disable-next-line no-param-reassign
    flightOffer.FlightCombinations = availableFlightCombinations;
    // eslint-disable-next-line no-param-reassign
    flightOffer.CurrentCombinations = getFlightCombinationsTree(
      availableFlightCombinations
    );
  }

  return ret;
};

export default filterAirports;
